import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Plus, Minus, Trash2, ShoppingCart, User as UserIcon } from 'lucide-react';
import { fetchCategories } from '../../redux/slices/categorySlice';
import { fetchProducts } from '../../redux/slices/productSlice';
import { fetchUsers } from '../../redux/slices/userSlice';
import { createOrder } from '../../redux/slices/orderSlice';
import toast from 'react-hot-toast';

const AdminOrder = () => {
    const dispatch = useDispatch();
    const { categories } = useSelector((state) => state.category);
    const { products } = useSelector((state) => state.product);
    const { users } = useSelector((state) => state.user);
    const { loading: orderLoading } = useSelector((state) => state.order);

    const [selectedCategory, setSelectedCategory] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [cart, setCart] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [showUserDropdown, setShowUserDropdown] = useState(false);

    // Fetch data on mount
    useEffect(() => {
        dispatch(fetchCategories());
        dispatch(fetchProducts());
        dispatch(fetchUsers());
    }, [dispatch]);

    // Filter products by selected category and search term
    const filteredProducts = products.filter((product) => {
        const matchesCategory = selectedCategory ? product.categoryId === selectedCategory : true;
        const matchesSearch =
            product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.productCode.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCategory && matchesSearch && product.isActive;
    });

    // Filter users by search term
    const filteredUsers = users.filter(
        (user) =>
            user.displayName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            user.mobileNumber?.includes(userSearchTerm) ||
            user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()),
    );

    // Add product to cart
    const addToCart = (product) => {
        const existingItem = cart.find((item) => item.productId === product.id);

        if (existingItem) {
            // Check stock availability
            if (existingItem.quantity >= product.stockCount) {
                toast.error('Not enough stock available');
                return;
            }
            setCart(
                cart.map((item) =>
                    item.productId === product.id ? { ...item, quantity: item.quantity + 1 } : item,
                ),
            );
            toast.success('Quantity updated');
        } else {
            if (product.stockCount <= 0) {
                toast.error('Product out of stock');
                return;
            }
            setCart([
                ...cart,
                {
                    productId: product.id,
                    productName: product.name,
                    productCode: product.productCode,
                    price: parseFloat(product.finalPrice) || 0,
                    quantity: 1,
                    maxStock: product.stockCount,
                },
            ]);
            toast.success('Product added to cart');
        }
    };

    // Update quantity
    const updateQuantity = (productId, delta) => {
        setCart(
            cart
                .map((item) => {
                    if (item.productId === productId) {
                        const newQuantity = item.quantity + delta;
                        if (newQuantity <= 0) return null;
                        if (newQuantity > item.maxStock) {
                            toast.error('Not enough stock available');
                            return item;
                        }
                        return { ...item, quantity: newQuantity };
                    }
                    return item;
                })
                .filter(Boolean),
        );
    };

    // Remove from cart
    const removeFromCart = (productId) => {
        setCart(cart.filter((item) => item.productId !== productId));
        toast.success('Product removed from cart');
    };

    // Calculate totals
    const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const gst = subtotal * 0.18; // 18% GST
    const total = subtotal + gst;

    // Select user from dropdown
    const selectUser = (user) => {
        setSelectedUser(user);
        setUserSearchTerm(user.displayName || user.email);
        setShowUserDropdown(false);
    };

    // Place order
    const handlePlaceOrder = async () => {
        if (!selectedUser) {
            toast.error('Please select a user');
            return;
        }

        if (cart.length === 0) {
            toast.error('Cart is empty');
            return;
        }

        const orderData = {
            userId: selectedUser.id,
            userName: selectedUser.displayName || selectedUser.email,
            userEmail: selectedUser.email,
            userMobile: selectedUser.mobileNumber || '',
            items: cart,
            subtotal: subtotal.toFixed(2),
            gst: gst.toFixed(2),
            total: total.toFixed(2),
            paymentMethod,
            status: 'pending',
            orderDate: new Date().toISOString(),
        };

        try {
            await dispatch(createOrder(orderData)).unwrap();
            toast.success('Order placed successfully');

            // Reset form
            setCart([]);
            setSelectedUser(null);
            setUserSearchTerm('');
            setPaymentMethod('cash');

            // Refresh products to get updated stock
            dispatch(fetchProducts());
        } catch (error) {
            toast.error('Failed to place order');
            console.error('Error:', error);
        }
    };

    return (
        <div className='p-4 md:p-6'>
            <div className='mb-6'>
                <h1 className='text-2xl md:text-3xl font-bold text-gray-900 mb-2'>Create Order</h1>
                <p className='text-gray-600'>Select products and create orders for users</p>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-12 gap-4'>
                {/* Left Column - Categories */}
                <div className='lg:col-span-3'>
                    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
                        <h2 className='text-lg font-semibold text-gray-900 mb-4'>Categories</h2>
                        <div className='space-y-2'>
                            <button
                                onClick={() => setSelectedCategory(null)}
                                className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                                    selectedCategory === null
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                All Categories
                            </button>
                            {categories.map((category) => (
                                <button
                                    key={category.id}
                                    onClick={() => setSelectedCategory(category.id)}
                                    className={`w-full text-left px-4 py-3 rounded-lg transition-all ${
                                        selectedCategory === category.id
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <div className='font-medium'>{category.name}</div>
                                    {category.description && (
                                        <div className='text-xs mt-1 opacity-80 truncate'>
                                            {category.description}
                                        </div>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Center Column - Products */}
                <div className='lg:col-span-5'>
                    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
                        <div className='mb-4'>
                            <h2 className='text-lg font-semibold text-gray-900 mb-3'>Products</h2>
                            <div className='relative'>
                                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                                <input
                                    type='text'
                                    placeholder='Search products...'
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all'
                                />
                            </div>
                        </div>

                        <div className='space-y-2 max-h-[600px] overflow-y-auto'>
                            {filteredProducts.length === 0 ? (
                                <div className='text-center py-8 text-gray-500'>
                                    No products found
                                </div>
                            ) : (
                                filteredProducts.map((product) => (
                                    <div
                                        key={product.id}
                                        className='border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-all'
                                    >
                                        <div className='flex justify-between items-start mb-2'>
                                            <div className='flex-1'>
                                                <h3 className='font-semibold text-gray-900 flex items-center gap-1'>
                                                    {product.name}
                                                    {product.isSpecial && (
                                                        <span className="text-amber-500" title="Special Product">★</span>
                                                    )}
                                                </h3>
                                                <p className='text-xs text-gray-500'>
                                                    {product.productCode}
                                                </p>
                                            </div>
                                            <div className='text-right'>
                                                <div className='text-lg font-bold text-green-600'>
                                                    ₹{product.finalPrice || 0}
                                                </div>
                                                {product.discount > 0 && (
                                                    <div className='text-xs text-gray-500 line-through'>
                                                        ₹{product.mrp}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='flex justify-between items-center'>
                                            <div className='text-sm text-gray-600'>
                                                Stock:{' '}
                                                <span className='font-semibold'>
                                                    {product.stockCount}
                                                </span>{' '}
                                                {product.uom}
                                            </div>
                                            <button
                                                onClick={() => addToCart(product)}
                                                disabled={product.stockCount <= 0}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                                    product.stockCount <= 0
                                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                                }`}
                                            >
                                                Add to Cart
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - User Selection & Cart */}
                <div className='lg:col-span-4'>
                    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
                        {/* User Selection */}
                        <div className='mb-6'>
                            <h2 className='text-lg font-semibold text-gray-900 mb-3'>
                                Select User
                            </h2>
                            <div className='relative'>
                                <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
                                <input
                                    type='text'
                                    placeholder='Search by name, mobile, or email...'
                                    value={userSearchTerm}
                                    onChange={(e) => {
                                        setUserSearchTerm(e.target.value);
                                        setShowUserDropdown(true);
                                    }}
                                    onFocus={() => setShowUserDropdown(true)}
                                    className='w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all'
                                />

                                {/* User Dropdown */}
                                {showUserDropdown && userSearchTerm && filteredUsers.length > 0 && (
                                    <div className='absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto'>
                                        {filteredUsers.map((user) => (
                                            <button
                                                key={user.id}
                                                onClick={() => selectUser(user)}
                                                className='w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0'
                                            >
                                                <div className='font-medium text-gray-900'>
                                                    {user.displayName || 'No Name'}
                                                </div>
                                                <div className='text-sm text-gray-600'>
                                                    {user.mobileNumber}
                                                </div>
                                                <div className='text-xs text-gray-500'>
                                                    {user.email}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Selected User Info */}
                            {selectedUser && (
                                <div className='mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200'>
                                    <div className='flex items-start gap-3'>
                                        <div className='w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white flex-shrink-0'>
                                            <UserIcon className='w-5 h-5' />
                                        </div>
                                        <div className='flex-1 min-w-0'>
                                            <h3 className='font-semibold text-gray-900 truncate'>
                                                {selectedUser.displayName || 'No Name'}
                                            </h3>
                                            <p className='text-sm text-gray-600'>
                                                {selectedUser.mobileNumber}
                                            </p>
                                            <div className='mt-2 grid grid-cols-2 gap-2 text-xs'>
                                                {selectedUser.weight && (
                                                    <div>
                                                        <span className='text-gray-600'>
                                                            Weight:
                                                        </span>{' '}
                                                        <span className='font-semibold'>
                                                            {selectedUser.weight} kg
                                                        </span>
                                                    </div>
                                                )}
                                                {selectedUser.height && selectedUser.weight && (
                                                    <div>
                                                        <span className='text-gray-600'>BMI:</span>{' '}
                                                        <span className='font-semibold'>
                                                            {(
                                                                selectedUser.weight /
                                                                Math.pow(
                                                                    selectedUser.height / 100,
                                                                    2,
                                                                )
                                                            ).toFixed(1)}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Cart */}
                        <div className='border-t border-gray-200 pt-4'>
                            <div className='flex items-center justify-between mb-4'>
                                <h2 className='text-lg font-semibold text-gray-900 flex items-center gap-2'>
                                    <ShoppingCart className='w-5 h-5' />
                                    Cart ({cart.length})
                                </h2>
                                {cart.length > 0 && (
                                    <button
                                        onClick={() => setCart([])}
                                        className='text-sm text-red-600 hover:text-red-700'
                                    >
                                        Clear All
                                    </button>
                                )}
                            </div>

                            <div className='space-y-3 mb-4 max-h-64 overflow-y-auto'>
                                {cart.length === 0 ? (
                                    <div className='text-center py-8 text-gray-500'>
                                        Cart is empty
                                    </div>
                                ) : (
                                    cart.map((item) => (
                                        <div
                                            key={item.productId}
                                            className='border border-gray-200 rounded-lg p-3 bg-gray-50'
                                        >
                                            <div className='flex justify-between items-start mb-2'>
                                                <div className='flex-1 pr-2'>
                                                    <h4 className='font-medium text-gray-900 text-sm'>
                                                        {item.productName}
                                                    </h4>
                                                    <p className='text-xs text-gray-500'>
                                                        {item.productCode}
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() => removeFromCart(item.productId)}
                                                    className='text-red-600 hover:text-red-700'
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </button>
                                            </div>

                                            <div className='flex justify-between items-center'>
                                                <div className='flex items-center gap-2'>
                                                    <button
                                                        onClick={() =>
                                                            updateQuantity(item.productId, -1)
                                                        }
                                                        className='w-7 h-7 bg-white border border-gray-300 rounded-md flex items-center justify-center hover:bg-gray-100'
                                                    >
                                                        <Minus className='w-3 h-3' />
                                                    </button>
                                                    <span className='w-8 text-center font-semibold'>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            updateQuantity(item.productId, 1)
                                                        }
                                                        className='w-7 h-7 bg-white border border-gray-300 rounded-md flex items-center justify-center hover:bg-gray-100'
                                                    >
                                                        <Plus className='w-3 h-3' />
                                                    </button>
                                                </div>
                                                <div className='text-right'>
                                                    <div className='text-sm font-semibold text-gray-900'>
                                                        ₹{(item.price * item.quantity).toFixed(2)}
                                                    </div>
                                                    <div className='text-xs text-gray-500'>
                                                        ₹{item.price} each
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Order Summary */}
                            {cart.length > 0 && (
                                <>
                                    <div className='border-t border-gray-200 pt-4 space-y-2 mb-4'>
                                        <div className='flex justify-between text-sm'>
                                            <span className='text-gray-600'>Subtotal</span>
                                            <span className='font-semibold'>
                                                ₹{subtotal.toFixed(2)}
                                            </span>
                                        </div>
                                        <div className='flex justify-between text-sm'>
                                            <span className='text-gray-600'>GST (18%)</span>
                                            <span className='font-semibold'>₹{gst.toFixed(2)}</span>
                                        </div>
                                        <div className='flex justify-between text-lg font-bold border-t border-gray-200 pt-2'>
                                            <span>Total</span>
                                            <span className='text-green-600'>
                                                ₹{total.toFixed(2)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Payment Method */}
                                    <div className='mb-4'>
                                        <label className='block text-sm font-semibold text-gray-700 mb-2'>
                                            Payment Method
                                        </label>
                                        <select
                                            value={paymentMethod}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white'
                                        >
                                            <option value='cash'>Cash</option>
                                            <option value='upi'>UPI</option>
                                            <option value='card'>Card</option>
                                            <option value='online'>Online Payment</option>
                                        </select>
                                    </div>

                                    {/* Place Order Button */}
                                    <button
                                        onClick={handlePlaceOrder}
                                        disabled={orderLoading || !selectedUser}
                                        className={`w-full py-3 rounded-lg font-semibold transition-all ${
                                            orderLoading || !selectedUser
                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                : 'bg-green-600 text-white hover:bg-green-700'
                                        }`}
                                    >
                                        {orderLoading ? 'Placing Order...' : 'Place Order'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminOrder;
